/**
 * The grid of locker doors drawn over the bovenbouw image.
 *
 * Micrio markers are named `kluis-{row}-{col}`, row 1 top and column 1 left.
 * Each row is one theme and the artwork is per-theme, so the marker name alone
 * decides which image to draw.
 */
import betrokkenheid from '@/assets/images/lockers/betrokkenheid.webp'
import democratie from '@/assets/images/lockers/democratie.webp'
import identiteit from '@/assets/images/lockers/identiteit.webp'
import mediawijsheid from '@/assets/images/lockers/mediawijsheid.webp'
import samenleven from '@/assets/images/lockers/samenleven.webp'
import wereldburgers from '@/assets/images/lockers/wereldburgers.webp'
import betrokkenheidOpen from '@/assets/images/lockers/betrokkenheid-open.webp'
import democratieOpen from '@/assets/images/lockers/democratie-open.webp'
import identiteitOpen from '@/assets/images/lockers/identiteit-open.webp'
import mediawijsheidOpen from '@/assets/images/lockers/mediawijsheid-open.webp'
import samenlevenOpen from '@/assets/images/lockers/samenleven-open.webp'
import wereldburgersOpen from '@/assets/images/lockers/wereldburgers-open.webp'
import deur01 from '@/assets/images/lockers/deur-01.webp'
import deur06 from '@/assets/images/lockers/deur-06.webp'
import deur08 from '@/assets/images/lockers/deur-08.webp'
import deur11 from '@/assets/images/lockers/deur-11.webp'
import deur16 from '@/assets/images/lockers/deur-16.webp'
import deur20 from '@/assets/images/lockers/deur-20.webp'
import deur21 from '@/assets/images/lockers/deur-21.webp'
import deur24 from '@/assets/images/lockers/deur-24.webp'
import deur26 from '@/assets/images/lockers/deur-26.webp'

export const LOCKER_ROWS = 6
export const LOCKER_COLS = 5

/** Leading `null` keeps this 1-based, matching the marker names. */
const THEME_BY_ROW = [
	null,
	'identiteit',
	'samenleven',
	'betrokkenheid',
	'democratie',
	'mediawijsheid',
	'wereldburgers',
] as const

export type LockerTheme = Exclude<(typeof THEME_BY_ROW)[number], null>

const IMAGE_BY_THEME: Record<LockerTheme, string> = {
	identiteit,
	samenleven,
	betrokkenheid,
	democratie,
	mediawijsheid,
	wereldburgers,
}

const IMAGE_OPEN_BY_THEME: Record<LockerTheme, string> = {
	identiteit: identiteitOpen,
	samenleven: samenlevenOpen,
	betrokkenheid: betrokkenheidOpen,
	democratie: democratieOpen,
	mediawijsheid: mediawijsheidOpen,
	wereldburgers: wereldburgersOpen,
}

/**
 * Doors with bespoke artwork, keyed by locker number. These already have the
 * number drawn on the plate, so they must NOT also get a rendered one.
 *
 * Note the plate sits ~8% of the panel width further right in this batch than in
 * the themed artwork. That only matters because it rules out reusing
 * PLATE_IN_ARTWORK for them — which we never do, the number being baked in.
 */
const IMAGE_BY_NUMBER: Record<number, string> = {
	1: deur01,
	6: deur06,
	8: deur08,
	11: deur11,
	16: deur16,
	20: deur20,
	21: deur21,
	24: deur24,
	26: deur26,
}

export interface LockerRef {
	row: number
	col: number
	theme: LockerTheme
	image: string
	imageOpen: string
	/** 1..30, numbered left to right then top to bottom. */
	number: number
	/** Zero-padded, matching how the bespoke artwork is lettered. */
	numberLabel: string
	/** True when `image` already has the number drawn on it. */
	hasBakedNumber: boolean
}

export const LOCKER_COUNT = LOCKER_ROWS * LOCKER_COLS

/** The canonical marker title, and our map key everywhere. */
export function lockerKey(number: number): string {
	return `kluis-${number}`
}

export function lockerNumber(row: number, col: number): number {
	return (row - 1) * LOCKER_COLS + col
}

const TITLE_PATTERN = /^kluis-(\d+)$/
const SUBMARKER_PATTERN = /^kluis-(\d+)_(.+)$/
// Superseded by the flat numbering; kept so the app keeps working while the
// markers are renamed in the Micrio dashboard, which cannot be done atomically.
// Drop these two once no `kluis-{row}-{col}` titles remain.
const LEGACY_TITLE_PATTERN = /^kluis-(\d+)-(\d+)$/
const LEGACY_SUBMARKER_PATTERN = /^kluis-(\d+)-(\d+)_(.+)$/

const inRange = (n: number) => (Number.isInteger(n) && n >= 1 && n <= LOCKER_COUNT ? n : undefined)

/**
 * Bounds-checked per axis, not just on the resulting number: `kluis-1-6` has no
 * sixth column, but would otherwise land on locker 6 — silently binding a typo to
 * the wrong locker.
 */
function legacyNumber(row: number, col: number): number | undefined {
	if (row < 1 || row > LOCKER_ROWS || col < 1 || col > LOCKER_COLS) return undefined
	return inRange(lockerNumber(row, col))
}

function numberFromTitle(clean: string): number | undefined {
	const flat = TITLE_PATTERN.exec(clean)
	if (flat) return inRange(Number(flat[1]))
	const legacy = LEGACY_TITLE_PATTERN.exec(clean)
	if (!legacy) return undefined
	return legacyNumber(Number(legacy[1]), Number(legacy[2]))
}

export function parseLockerTitle(title: string): LockerRef | undefined {
	const number = numberFromTitle(title.trim().toLowerCase())
	if (!number) return undefined

	const row = Math.ceil(number / LOCKER_COLS)
	const col = number - (row - 1) * LOCKER_COLS
	const theme = THEME_BY_ROW[row]
	if (!theme) return undefined

	const bespoke = IMAGE_BY_NUMBER[number]

	return {
		row,
		col,
		theme,
		image: bespoke ?? IMAGE_BY_THEME[theme],
		imageOpen: IMAGE_OPEN_BY_THEME[theme],
		number,
		numberLabel: String(number).padStart(2, '0'),
		hasBakedNumber: Boolean(bespoke),
	}
}

export interface SubmarkerRef {
	number: number
	/** The bit after the underscore — `video`, `info`, … */
	name: string
	parentKey: string
}

/**
 * `kluis-{number}_{name}` — separate markers for other content, carrying no
 * clickableArea. Micrio owns their clicks; we only decide whether they show.
 */
export function parseSubmarkerTitle(title: string): SubmarkerRef | undefined {
	const clean = title.trim().toLowerCase()

	const flat = SUBMARKER_PATTERN.exec(clean)
	if (flat) {
		const number = inRange(Number(flat[1]))
		return number ? { number, name: flat[2], parentKey: lockerKey(number) } : undefined
	}

	const legacy = LEGACY_SUBMARKER_PATTERN.exec(clean)
	if (!legacy) return undefined
	const number = legacyNumber(Number(legacy[1]), Number(legacy[2]))
	return number ? { number, name: legacy[3], parentKey: lockerKey(number) } : undefined
}

/**
 * Where the door panel sits inside the artwork, as fractions of the whole image.
 *
 * A marker's clickableArea traces the panel — the black-outlined square — not the
 * artwork, whose handle sticks out to the left. Measured off the source PNG
 * (1198x1073): the panel spans x 145..1194, y 4..1065.
 *
 * Re-measure if the artwork is ever re-exported.
 */
export const PANEL_IN_ARTWORK = {
	x: 145 / 1198,
	y: 4 / 1073,
	w: 1050 / 1198,
	h: 1062 / 1073,
} as const

/**
 * The same for the open artwork, which is cropped to the DOOR ALONE — the
 * cabinet interior was removed because the Micrio background already shows it,
 * and an opaque interior here covered the submarkers.
 *
 * So the box these describe (the cabinet opening, i.e. the marker) lies entirely
 * OUTSIDE the cropped image, hence the negative `x` and `w`/`h` above 1;
 * artworkRectFromPanel is linear and handles that unchanged.
 *
 * Derived from the source renders (3520x2970, all six identical), where the door
 * occupies x 1996..3436, y 40..2906 — the crop — and the cabinet box spans
 * x 1.82%..56.6%, y 14.29%..81.1% of the original.
 *
 * Sanity check: these put the door's left edge at panel.x0 + 1.002 * panelWidth,
 * the marker's right edge, which is exactly where the door hinges.
 */
export const PANEL_IN_ARTWORK_OPEN = {
	x: -1.3416,
	y: 0.1341,
	w: 1.3388,
	h: 0.6928,
} as const

/**
 * The number plate on the THEMED closed artwork, as fractions of the whole
 * image — where the rendered number goes. Measured as the largest solid dark
 * region at x 587, y 171, 433x183 in the 1198x1072 artwork; identical across all
 * six themes.
 *
 * The open artwork is the inside of the door and carries no plate, which is why
 * the number only ever exists on the closed face.
 */
export const PLATE_IN_ARTWORK = {
	x: 587 / 1198,
	y: 171 / 1072,
	w: 433 / 1198,
	h: 183 / 1072,
} as const

/**
 * Cap height of the digits as a fraction of the plate height, measured off the
 * bespoke artwork (0.77 and 0.761 on the two doors with unobstructed plates).
 * Converted to a font-size by PLATE_FONT_SCALE.
 */
export const PLATE_DIGIT_HEIGHT = 0.765
